import React, { createContext, useReducer, useEffect } from "react";
import PropTypes from "prop-types";
import { useUser } from "~/assets/js/Api/userApi";
import { isEqual } from "lodash";
import { ToastContainer } from "../../components/Toast";
import CheckoutSession from "./CheckoutSession";
import useRemoveDiscountListener from "../../Helpers/hooks/useRemoveDiscountListener";
import i18next from "i18next";

// The component that will provide the value of the (stored) context for the entire checkout page

export const ACTIONS = {
    CHANGE_PAGE: "CHANGE_PAGE",
    PREVIOUS_PAGE: "PREVIOUS_PAGE",
    LOGIN: "LOGIN",
    ADD_TOAST: "ADD_TOAST",
    REMOVE_TOAST: "REMOVE_TOAST",
    ADD_TIME_SLOT: "ADD_TIME_SLOT",
    ADD_EARLY_ON_LOCATION: "ADD_EARLY_ON_LOCATION",
    ADD_ORDER_ID: "ADD_ORDER_ID",
    SAVE_ORDER_ADDRESS: "SAVE_ORDER_ADDRESS",
    IS_ORDER_FOR_COMPANY: "IS_ORDER_FOR_COMPANY",
    RESET_AFTER_LOCATION_CHANGE: "RESET_AFTER_LOCATION_CHANGE",
    UPDATE_COMPENSATION: "UPDATE_COMPENSATION",
    UPDATE_PAYMENT: "UPDATE_PAYMENT",
    TOGGLE_REUSABLE_BOWLS: "TOGGLE_REUSABLE_BOWLS",
};

export const PAGES = {
    CART: "cart",
    LOGIN: "login",
    REGISTER: "register",
    CONTACT_DETAILS: "contactDetails",
    PAYMENT_SELECT: "paymentSelect",
    TIME_SLOT_SELECT: "timeSlotSelect",
    ORDER_OVERVIEW: "orderOverview",
    MEMBER_THANK_YOU: "memberThankYou",
};

const ROUTER = {
    cart: {
        prevPage: PAGES.CART,
        nextPage: PAGES.LOGIN,
    },
    login: {
        prevPage: PAGES.CART,
        nextPage: PAGES.CONTACT_DETAILS,
    },
    register: {
        prevPage: PAGES.CART,
        nextPage: PAGES.CONTACT_DETAILS,
    },
    contactDetails: {
        prevPage: PAGES.CART,
        nextPage: PAGES.PAYMENT_SELECT,
    },
    paymentSelect: {
        prevPage: PAGES.CONTACT_DETAILS,
        nextPage: PAGES.TIME_SLOT_SELECT,
    },
    timeSlotSelect: {
        prevPage: PAGES.PAYMENT_SELECT,
        nextPage: PAGES.ORDER_OVERVIEW,
    },
    orderOverview: {
        prevPage: PAGES.TIME_SLOT_SELECT,
        nextPage: "",
    },
    memberThankYou: {
        prevPage: "",
        nextPage: "",
    },
};

const TOAST_VARIANTS = ["success", "info", "danger", "warning"];

const CheckoutContext = createContext({});

const CheckoutReducer = (state, action) => {
    const { type, payload } = action;
    switch (type) {
        case ACTIONS.CHANGE_PAGE:
            const { nextPage } = ROUTER[state.page];
            window.scrollTo(0, 0);

            if (payload) {
                if (!Object.values(PAGES).includes(payload)) {
                    return { ...state, page: PAGES.CART };
                }
                return {
                    ...state,
                    page: payload,
                };
            }

            return {
                ...state,
                page: nextPage,
            };
        case ACTIONS.PREVIOUS_PAGE:
            const { prevPage } = ROUTER[state.page];
            window.scrollTo(0, 0);

            return {
                ...state,
                page: prevPage,
            };
        case ACTIONS.ADD_TOAST:
            const { variant, title, text, CTA } = payload;
            const newToast = {
                id: new Date().valueOf(), // unique id for toasts
                variant: TOAST_VARIANTS.includes(variant) ? variant : "success",
                title,
                text,
                CTA,
            };
            return { ...state, toasts: [...state.toasts, newToast] };
        case ACTIONS.REMOVE_TOAST:
            const { id } = payload;
            return {
                ...state,
                toasts: state.toasts.filter((toast) => toast.id !== id),
            };
        case ACTIONS.LOGIN:
            return { ...state, user: payload };
        case ACTIONS.ADD_TIME_SLOT:
            return { ...state, timeSlot: payload };
        case ACTIONS.ADD_ORDER_ID:
            return { ...state, orderId: payload };
        case ACTIONS.SAVE_ORDER_ADDRESS:
            return { ...state, orderAddress: payload };
        case ACTIONS.IS_ORDER_FOR_COMPANY:
            return { ...state, isCompanyOrder: payload };
        case ACTIONS.RESET_AFTER_LOCATION_CHANGE:
            return { ...state, timeSlot: null, paymentOption: null };
        case ACTIONS.UPDATE_PAYMENT:
            return { ...state, paymentOption: payload };
        case ACTIONS.TOGGLE_REUSABLE_BOWLS:
            return { ...state, reusableBowls: payload };
        default:
            throw new Error();
    }
};

const CheckoutProvider = ({ children }) => {
    const [state, dispatch] = useReducer(
        CheckoutReducer,
        CheckoutSession.initialState,
        CheckoutSession.init
    );
    const { user } = useUser();

    if (!isEqual(user, state.user)) {
        dispatch({ type: ACTIONS.LOGIN, payload: user });
    }

    useEffect(() => {
        CheckoutSession.update(state);
    }, [state]);

    useRemoveDiscountListener(() => {
        dispatch({
            type: ACTIONS.ADD_TOAST,
            payload: {
                variant: "warning",
                title: i18next.t("toasts.remove_coupon.title_success"),
                text: "",
            },
        });
    });

    return (
        <CheckoutContext.Provider value={{ state, dispatch }}>
            <ToastContainer
                toasts={state.toasts}
                onCancel={(id) =>
                    dispatch({ type: ACTIONS.REMOVE_TOAST, payload: { id } })
                }
            />
            {children}
        </CheckoutContext.Provider>
    );
};

CheckoutProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};

export { CheckoutProvider, CheckoutContext };
